import React, { useRef, useState } from 'react';
import { Col, Form, Row } from 'antd';
import { useNavigate } from 'react-router-dom';
import styled from 'styled-components';

import { handleFormErrors } from '../../common/errorHelpers';
import { useAddCandidate } from '../../common/useAddCandidate';
import { useAddCandidateNote } from '../../common/useAddCandidateNote';
import { useHeaderHeight } from '../../common/useHeaderHeight';
import { useTableDataHeight } from '../../common/useTableDataHeight';
import {
  CandidateAdditionalDataForm,
  CandidateDataForm,
  CandidateStartingNoteForm,
  mapErrorFieldsToFormLabels,
} from '../../components/CandidateDataForm';
import { CandidateDuplicatesProvider } from '../../components/CandidateDuplicatesContext';
import { pathToUrl } from '../../utils/router';
import { PageRoutesEnum } from '../../utils/routes';

import { candidateEditAiRefreshNavigationState } from './candidateAiRefreshNavigation';
import { CandidateDetailsPageHeader } from './CandidateDetailsPageHeader';
import { buildCandidateFormPayload } from './candidateFormValues';
import { CandidatesPersonalData } from './CandidatesPersonalData';

const MainContainer = styled.div`
  display: flex;
  flex-direction: column;
  padding: 20px;
`;

export const AddCandidatePage: React.FC = () => {
  const [form] = Form.useForm();
  const isSubmitting = useRef(false);

  const addCandidate = useAddCandidate();
  const addNote = useAddCandidateNote();
  const navigate = useNavigate();

  const [cvAttachement, setCvAttachement] = useState<string | undefined>();

  const { headerHeight, headerRef } = useHeaderHeight();

  const { tableHeight } = useTableDataHeight(headerHeight + 20, 20);

  const handleSave = async () => {
    if (isSubmitting.current || addCandidate.isLoading) return;

    try {
      await form.validateFields();
    } catch {
      return;
    }

    isSubmitting.current = true;

    const { note, ...dataValues } = form.getFieldsValue();
    addCandidate.mutateAsync(
      { data: buildCandidateFormPayload(dataValues, cvAttachement) },
      {
        onSuccess: (record) => {
          if (note) {
            addNote.mutate({ queryParams: { id: record.data.uuid }, data: { content: note } });
          }
          navigate(pathToUrl(PageRoutesEnum.candidateEdit, { id: record.data.uuid }), {
            state: cvAttachement ? candidateEditAiRefreshNavigationState : undefined,
          });
        },
        onError: (error) => {
          handleFormErrors({ error, form, mapErrorFieldsToFormLabels });
        },
        onSettled: () => {
          isSubmitting.current = false;
        },
      }
    );
  };

  const handleCancel = () => navigate(-1);

  return (
    <CandidateDuplicatesProvider>
      <MainContainer>
        <div ref={headerRef}>
          <CandidateDetailsPageHeader
            title="Add candidate"
            disabled={addCandidate.isLoading}
            onSave={handleSave}
            onCancel={handleCancel}
          />
        </div>
        <Row gutter={[15, 15]}>
          <Col xs={24} xl={14}>
            <CandidateDataForm form={form} />
            <CandidateAdditionalDataForm form={form} />
            <CandidateStartingNoteForm form={form} />
          </Col>
          <Col xs={24} xl={10}>
            <CandidatesPersonalData
              cvAttachement={cvAttachement}
              addCvAttachement={setCvAttachement}
              height={tableHeight}
            />
          </Col>
        </Row>
      </MainContainer>
    </CandidateDuplicatesProvider>
  );
};
